/**
 * OpenSection — Sidebar section showing HITL requests awaiting human input.
 *
 * Auto-hides when empty. Items sorted by urgency (least remaining time first).
 * Clicking an item navigates to the corresponding mission.
 *
 * Visual style matches admin-ui sidebar sections.
 */

import React from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ShieldAlert } from 'lucide-react'
import { Badge, Button, Text, cn } from '@agion/ui'
import { useExecutionStore, type GlobalHITLRequest, type GlobalGovernanceAlert } from '../../hooks/useExecutionStore'
import { useCountdown } from '../../hooks/useCountdown'
import { questionCountdown } from '../../utils/questionOptions'

const URGENT_THRESHOLD_MS = 30_000

function HITLSidebarItem({ request }: { request: GlobalHITLRequest }) {
  const navigate = useNavigate()
  const { from, lastsMs } = questionCountdown(request)
  const countdown = useCountdown(from, lastsMs)

  const isUrgent = lastsMs != null && from + lastsMs - Date.now() < URGENT_THRESHOLD_MS

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
    >
      <Button
        variant="ghost"
        onClick={() => navigate(`/mission/${request.missionId}${request.executionId ? `?execution=${request.executionId}` : ''}`)}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors h-auto text-left',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1',
          'text-slate-700 dark:text-slate-300 hover:bg-white/80 dark:hover:bg-slate-800/50'
        )}
      >
        {/* Pulsing dot */}
        <span className="w-5 flex items-center justify-center flex-shrink-0">
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full animate-pulse',
              isUrgent ? 'bg-red-500' : 'bg-amber-500'
            )}
          />
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Text size="sm" weight="medium" className="truncate block">
            {request.missionName}
          </Text>
          <Text size="xs" color="muted" className="truncate block">
            {request.prompt}
          </Text>
        </div>

        {/* Countdown */}
        {countdown && (
          <span
            className={cn(
              'text-xs font-mono flex-shrink-0',
              isUrgent
                ? 'text-red-500 dark:text-red-400'
                : 'text-amber-600 dark:text-amber-400'
            )}
          >
            {countdown}
          </span>
        )}
      </Button>
    </motion.div>
  )
}

function GovernanceSidebarItem({ alert }: { alert: GlobalGovernanceAlert }) {
  const navigate = useNavigate()

  const isEscalate = alert.action === 'escalate'

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
    >
      <Button
        variant="ghost"
        onClick={() => navigate(`/mission/${alert.missionId}`)}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors h-auto text-left',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1',
          'text-slate-700 dark:text-slate-300 hover:bg-white/80 dark:hover:bg-slate-800/50'
        )}
      >
        {/* Shield icon */}
        <span className="w-5 flex items-center justify-center flex-shrink-0">
          <ShieldAlert
            className={cn(
              'h-3.5 w-3.5',
              isEscalate
                ? 'text-amber-500 dark:text-amber-400'
                : 'text-rose-500 dark:text-rose-400'
            )}
          />
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Text size="sm" weight="medium" className="truncate block">
            {alert.missionName}
          </Text>
          <Text size="xs" color="muted" className="truncate block">
            {alert.reason || alert.ruleName || 'Policy decision'}
          </Text>
        </div>

        {/* Action badge */}
        <span
          className={cn(
            'text-xs font-medium flex-shrink-0 px-1.5 py-0.5 rounded',
            isEscalate
              ? 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-500/10'
              : 'text-rose-700 bg-rose-100 dark:text-rose-300 dark:bg-rose-500/10'
          )}
        >
          {isEscalate ? 'escalate' : 'deny'}
        </span>
      </Button>
    </motion.div>
  )
}

export function AttentionSection({ searchTerm = '' }: { searchTerm?: string }) {
  const { t } = useTranslation('chat')
  const getAttentionItemsSortedByUrgency = useExecutionStore(
    (s) => s.getAttentionItemsSortedByUrgency
  )
  const hitlRequests = useExecutionStore((s) => s.hitlRequests)
  const governanceAlerts = useExecutionStore((s) => s.governanceAlerts)

  // Re-derive sorted list when hitlRequests or governanceAlerts change, filtered by search
  const sortedItems = React.useMemo(() => {
    const all = getAttentionItemsSortedByUrgency()
    if (!searchTerm) return all
    const term = searchTerm.toLowerCase()
    return all.filter((item) => {
      if (item.itemType === 'hitl') {
        return (
          item.missionName?.toLowerCase().includes(term) ||
          item.prompt?.toLowerCase().includes(term)
        )
      }
      return (
        item.missionName?.toLowerCase().includes(term) ||
        item.reason?.toLowerCase().includes(term) ||
        item.ruleName?.toLowerCase().includes(term)
      )
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hitlRequests, governanceAlerts, getAttentionItemsSortedByUrgency, searchTerm])

  if (sortedItems.length === 0) return null

  const hasGovernance = sortedItems.some((i) => i.itemType === 'governance')

  return (
    <div className="mb-3">
      {/* Section header — premium sidebar pattern */}
      <div className="flex items-center gap-2 px-1 py-2">
        <span className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider flex-1">
          {t('needsResponse', 'Needs Your Response')}
        </span>
        <Badge variant={hasGovernance ? 'destructive' : 'warning'} size="sm">
          {sortedItems.length}
        </Badge>
      </div>

      <AnimatePresence>
        <div className="space-y-0.5">
          {sortedItems.map((item) =>
            item.itemType === 'hitl' ? (
              <HITLSidebarItem key={item.requestId} request={item} />
            ) : (
              <GovernanceSidebarItem key={item.id} alert={item} />
            )
          )}
        </div>
      </AnimatePresence>
    </div>
  )
}

// Backward compatibility alias
export { AttentionSection as OpenSection }
